import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  DropdownMenu,
  EllipsisVerticalIcon,
  IconButton,
  PencilIcon,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { NoteContentView, NoteItem } from '~publish/components/Notes'
import { canEditNoteAnnotations } from '~publish/hooks/notes/noteContent'
import { formatNoteTimestamp } from '~publish/components/Notes/noteTimestamp'
import type {
  CollaborationNote,
  CreatedNote,
} from '~publish/hooks/notes/notesQueries'

import styles from './PostNotes.module.css'
import { NoteReadStateIcon } from './NoteReadStateIcon'
import { EditPostNoteForm } from './EditPostNoteForm'
import { DeleteNoteDialog } from './DeleteNoteDialog'
import {
  AddReactionButton,
  NoteReactionList,
  QuickReactionButtons,
} from './NoteReactions'
import { usePostNotesContext } from './PostNotesProvider'

export function PostNote({
  note,
  currentAccountId,
  unreadStart = false,
}: {
  note: CollaborationNote | CreatedNote
  currentAccountId: string | null
  unreadStart?: boolean
}): JSX.Element {
  const {
    edit,
    react,
    remove,
    markRead,
    markUnread,
    selectionKey,
    tracksReadState,
  } = usePostNotesContext()
  const isEditing = edit.draft?.noteId === note.id
  const authorizedNote = 'allowedActions' in note ? note : null
  const canEdit =
    Boolean(authorizedNote?.allowedActions.includes('updateNote')) &&
    canEditNoteAnnotations(note.text, note.annotations)
  const canDelete = Boolean(
    authorizedNote?.allowedActions.includes('deleteNote'),
  )
  const canReact = Boolean(
    authorizedNote?.allowedActions.includes('reactToNote'),
  )
  const showToolbar = !isEditing
  const [pickerOpen, setPickerOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  // A reaction completes the toolbar's job, so it steps aside until the
  // pointer leaves the Note. Keyboard focus still shows it.
  const [dismissed, setDismissed] = useState(false)
  const toggleReaction = (emoji: string): void => {
    if (authorizedNote) void react.toggle(authorizedNote, emoji)
    setDismissed(true)
  }
  const [deleteSelectionKey, setDeleteSelectionKey] = useState<string | null>(
    null,
  )
  const deleteOpen = Boolean(
    selectionKey && deleteSelectionKey === selectionKey,
  )
  const actionsButtonRef = useRef<HTMLButtonElement>(null)
  const wasEditingRef = useRef(isEditing)
  useEffect(() => {
    if (wasEditingRef.current && !isEditing) actionsButtonRef.current?.focus()
    wasEditingRef.current = isEditing
  }, [isEditing])
  // Hiding the toolbar unmounts an open menu or picker without an
  // `onOpenChange(false)`, as Edit does, so the open state resets with it.
  // Otherwise the toolbar would come back pinned.
  useEffect(() => {
    if (showToolbar) return
    setMenuOpen(false)
    setPickerOpen(false)
  }, [showToolbar])
  useEffect(() => {
    if (deleteSelectionKey && deleteSelectionKey !== selectionKey) {
      setDeleteSelectionKey(null)
    }
  }, [deleteSelectionKey, selectionKey])
  const changeDeleteOpen = (open: boolean): void => {
    if (open) {
      setDeleteSelectionKey(selectionKey)
    } else {
      setDeleteSelectionKey(null)
      if (!remove.pending) {
        queueMicrotask(() => actionsButtonRef.current?.focus())
      }
    }
  }
  const authorName =
    note.author?.name?.trim() || note.author?.email?.trim() || 'Unknown author'
  const isCurrentUser =
    currentAccountId !== null && note.authorId === currentAccountId
  const isEdited = note.updatedAt !== note.createdAt
  const timestamp = formatNoteTimestamp(note.createdAt)

  return (
    <NoteItem
      data-notes-unread-start={unreadStart ? '' : undefined}
      className={styles.noteRow}
      onPointerLeave={() => setDismissed(false)}
    >
      <NoteItem.Avatar
        src={note.author?.avatar ?? undefined}
        name={authorName}
      />
      <NoteItem.Body>
        <NoteItem.Header>
          <NoteItem.Author title={authorName}>{authorName}</NoteItem.Author>
          {isCurrentUser && <span className={styles.selfMarker}>(you)</span>}
          <NoteItem.Timestamp dateTime={note.createdAt} title={timestamp.title}>
            {timestamp.label}
            {isEdited && <NoteItem.Edited> · edited</NoteItem.Edited>}
          </NoteItem.Timestamp>
          {showToolbar && (
            <div
              className={styles.toolbar}
              data-active={pickerOpen || menuOpen || undefined}
              data-dismissed={dismissed || undefined}
            >
              {canReact && (
                <div className={styles.toolbarReactions}>
                  <QuickReactionButtons
                    emojis={react.quickReactions}
                    onSelect={toggleReaction}
                  />
                  <AddReactionButton
                    variant="tertiary"
                    onSelect={toggleReaction}
                    onOpenChange={setPickerOpen}
                  />
                </div>
              )}
              <DropdownMenu
                modal={false}
                onOpenChange={setMenuOpen}
                trigger={
                  <IconButton
                    ref={actionsButtonRef}
                    label="Note actions"
                    size="small"
                    type="button"
                    variant="tertiary"
                    disabled={
                      Boolean(edit.draft) ||
                      remove.pending ||
                      markRead.pending ||
                      markUnread.pending
                    }
                  >
                    <EllipsisVerticalIcon size="xsmall" />
                  </IconButton>
                }
              >
                {canEdit && authorizedNote && (
                  <DropdownMenu.Item
                    onSelect={() => edit.start(authorizedNote)}
                  >
                    <PencilIcon />
                    Edit
                  </DropdownMenu.Item>
                )}
                {tracksReadState && (
                  <DropdownMenu.Item
                    disabled={markRead.pending || markUnread.pending}
                    onSelect={() => void markUnread.submit(note.id)}
                  >
                    <NoteReadStateIcon state="unread" />
                    Mark as unread
                  </DropdownMenu.Item>
                )}
                {canDelete && (
                  <DropdownMenu.Item
                    variant="critical"
                    onSelect={() => changeDeleteOpen(true)}
                  >
                    <TrashIcon />
                    Delete
                  </DropdownMenu.Item>
                )}
              </DropdownMenu>
            </div>
          )}
        </NoteItem.Header>
        {isEditing && 'allowedActions' in note ? (
          <EditPostNoteForm note={note} />
        ) : (
          <NoteItem.Content translate="no">
            {note.annotations.length &&
            canEditNoteAnnotations(note.text, note.annotations) ? (
              <NoteContentView
                content={{
                  text: note.text,
                  annotations: [...note.annotations],
                }}
              />
            ) : (
              note.text
            )}
          </NoteItem.Content>
        )}
        {!isEditing && (note.reactions.length > 0 || canReact) && (
          <NoteReactionList
            reactions={note.reactions}
            currentAccountId={currentAccountId}
            canReact={canReact}
            onToggle={toggleReaction}
          />
        )}
      </NoteItem.Body>
      {canDelete && authorizedNote && (
        <DeleteNoteDialog
          note={authorizedNote}
          open={deleteOpen}
          onOpenChange={changeDeleteOpen}
        />
      )}
    </NoteItem>
  )
}
